import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  operturiPainPoints,
  operturiModules,
  operturiSteps,
  operturiWhyChoose,
  operturiIntegrations,
  operturiPricingPlans,
  operturiFAQs,
  operturiScreenshots,
} from '@/mocks/suviData';
import SEOHead from '@/components/base/SEOHead';

export default function Operturi() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [activeFaqTab, setActiveFaqTab] = useState('Todos');

  const faqCategories = ['Todos', ...Array.from(new Set(operturiFAQs.map((f) => f.category)))];
  const filteredFAQs = activeFaqTab === 'Todos'
    ? operturiFAQs
    : operturiFAQs.filter((f) => f.category === activeFaqTab);

  return (
    <>
      <SEOHead
        title="Operturi - Software para Agencias de Viaje y Tour Operadores | Suvi Digital"
        description="Centraliza tu operación de reservas turísticas en una sola plataforma. Operturi: Motor de Reservas, Cotizador y Operaciones para agencias de viaje."
        keywords="software agencias viaje, gestion reservas turisticas, Operturi, CRM agencias viaje, cotizador viajes, motor reservas"
        canonical="https://suvidigital.com/operturi"
        schemaType="Product"
      />
      <main className="min-h-screen">
        {/* ===== HERO ===== */}
        <section className="relative min-h-[520px] md:min-h-[620px] flex items-center overflow-hidden">
          {/* Fondo pastel como home */}
          <div className="absolute inset-0 animate-gradient bg-gradient-to-br from-primary-100 via-background-50 to-secondary-100" />

          {/* Orbes de luz neón */}
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-accent-300/30 blur-[120px] animate-float-slow" />
          <div className="absolute bottom-[-15%] left-[-10%] w-[400px] h-[400px] rounded-full bg-primary-300/30 blur-[100px] animate-float-delay-1" />
          <div className="absolute top-1/3 left-1/2 w-72 h-72 rounded-full bg-secondary-300/25 blur-[100px] animate-float-delay-2" />

          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 xl:px-16 pt-24 md:pt-28">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-7xl mx-auto">
              <div>
                <span className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-md border border-primary-200/50 rounded-full px-4 py-2 mb-6">
                  <i className="ri-sparkling-line text-accent-500 text-sm" />
                  <span className="text-primary-700 text-sm font-medium tracking-wide">Software Propietario</span>
                </span>
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mb-5 leading-[1.1]">
                  Centraliza tu{' '}
                  <span className="text-gradient">operación turística</span> en una plataforma
                </h1>
                <p className="text-foreground-600 text-lg md:text-xl leading-relaxed max-w-lg mb-9">
                  Motor de Reservas, Cotizador y Operaciones. Todo integrado. Nada de Excel.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://wa.me/51989307864"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-400 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-300 whitespace-nowrap text-base overflow-hidden"
                  >
                    <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                    <i className="ri-whatsapp-line relative z-10" />
                    <span className="relative z-10">Solicitar Demo</span>
                  </a>
                  <Link
                    to="/contacto"
                    className="group inline-flex items-center justify-center gap-2 bg-white/70 hover:bg-white text-foreground-800 font-medium px-7 py-3.5 rounded-xl transition-all duration-300 border border-primary-200/50 hover:border-primary-300 whitespace-nowrap text-base backdrop-blur-sm"
                  >
                    Más Info
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20travel%20agency%20software%20dashboard%20mockup%20on%20laptop%20screen%20floating%20angle%2C%20clean%20UI%20with%20reservation%20calendar%20and%20pricing%20tables%2C%20light%20theme%2C%20minimal%20pastel%20blue%20and%20white%20colors%2C%20professional%20product%20photography%20with%20soft%20shadows%20on%20light%20gray%20background&width=900&height=600&seq=operturi-hero-dash&orientation=landscape"
                    alt="Dashboard de Operturi"
                    className="rounded-2xl shadow-2xl border border-background-200/60 w-full"
                  />
                  <div className="absolute -bottom-3 -left-3 bg-white/90 backdrop-blur-md rounded-xl p-3.5 shadow-lg border border-background-200/60">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center text-white text-sm">
                        <i className="ri-check-line" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">100+ agencias</p>
                        <p className="text-xs text-foreground-500">en Latinoamérica</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== PAIN POINTS ===== */}
        <section className="bg-white py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16 max-w-6xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-secondary-100 text-secondary-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-secondary-200/50">
                El Problema
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                ¿Tu agencia aún opera entre{' '}
                <span className="text-gradient">Excel, WhatsApp y múltiples sistemas</span>?
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {operturiPainPoints.map((point, i) => (
                <div
                  key={point.title}
                  className="card-lift bg-background-50 border border-background-200/60 rounded-2xl p-6 text-center group"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-600 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${point.icon} text-2xl`} />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">{point.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== THREE MODULES ===== */}
        <section className="bg-background-50 py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16 max-w-6xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
                Producto
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Tres módulos que{' '}
                <span className="text-gradient">transforman tu operación</span>
              </h2>
            </div>
            <div className="space-y-16">
              {operturiModules.map((mod, i) => (
                <div
                  key={mod.title}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="rounded-2xl overflow-hidden border border-background-200/60 shadow-lg group">
                      <img
                        src={mod.image}
                        alt={mod.title}
                        className="w-full h-64 md:h-80 object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  </div>
                  <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                    <span className="inline-block bg-accent-100 text-accent-700 text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
                      Módulo {i + 1}
                    </span>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                      {mod.title}
                    </h3>
                    <p className="text-foreground-500 text-base md:text-lg leading-relaxed mb-6">
                      {mod.description}
                    </p>
                    <a
                      href="https://wa.me/51989307864"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors"
                    >
                      Conocer más <i className="ri-arrow-right-line" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FOUR STEPS ===== */}
        <section className="bg-white py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16 max-w-6xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-secondary-100 text-secondary-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-secondary-200/50">
                Implementación
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Cuatro pasos para{' '}
                <span className="text-gradient">digitalizar tu agencia</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {operturiSteps.map((step, i) => (
                <div
                  key={step.title}
                  className="relative card-lift bg-background-50 border border-background-200/60 rounded-2xl p-6 text-center group"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100 to-accent-100 text-primary-600 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${step.icon} text-2xl`} />
                  </div>
                  <span className="inline-block bg-accent-100 text-accent-700 text-xs font-bold px-2 py-0.5 rounded mb-2">
                    {step.num}
                  </span>
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">{step.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{step.description}</p>
                  {i < operturiSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-background-300" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== WHY CHOOSE ===== */}
        <section className="bg-background-50 py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
                  Diferencial
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-5">
                  Por qué elegir{' '}
                  <span className="text-gradient">OPERTURI</span>
                </h2>
                <p className="text-foreground-500 text-base md:text-lg leading-relaxed mb-8">
                  Software hecho para agencias y operadores turísticos que entienden los procesos reales del sector.
                </p>
                <a
                  href="https://wa.me/51989307864"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-glow bg-primary-500 hover:bg-primary-600 text-white font-semibold px-8 py-4 rounded-xl inline-flex items-center gap-2 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line" />
                  Hablar con un asesor
                </a>
              </div>
              <div className="space-y-4">
                {operturiWhyChoose.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 bg-white rounded-xl p-4 border border-background-200/60"
                  >
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mt-0.5">
                      <i className="ri-check-line" />
                    </div>
                    <p className="text-foreground-600 text-sm md:text-base leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== STATS ===== */}
        <section className="bg-white py-14 md:py-20 border-y border-background-200/50">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { num: '2', suffix: ' países', label: 'en Latinoamérica' },
                { num: '100+', suffix: '', label: 'agencias activas' },
                { num: '4.5/5', suffix: '', label: 'satisfacción promedio' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-gradient">
                    {stat.num}{stat.suffix}
                  </p>
                  <p className="text-foreground-500 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== DESIGNED FOR ===== */}
        <section className="bg-background-50 py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16 max-w-6xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-secondary-100 text-secondary-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-secondary-200/50">
                Para quién es
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Diseñado para{' '}
                <span className="text-gradient">agencias y tour operadores</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card-lift bg-white border border-background-200/60 rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-5">
                  <i className="ri-building-2-line text-xl" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-foreground-900 mb-4">Agencias de Viaje</h3>
                <ul className="space-y-3">
                  {[
                    'Gestiona múltiples proveedores y destinos',
                    'Cotiza paquetes personalizados rápidamente',
                    'Controla comisiones y márgenes automáticamente',
                    'Envía propuestas digitales aprobadas online',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-foreground-500 text-sm">
                      <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-lift bg-white border border-background-200/60 rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-5">
                  <i className="ri-route-line text-xl" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-foreground-900 mb-4">Tour Operadores</h3>
                <ul className="space-y-3">
                  {[
                    'Administra tu inventario propio de tours',
                    'Controla disponibilidad en tiempo real',
                    'Gestiona reservas directas y de agencias',
                    'Optimiza ocupación y rentabilidad',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-foreground-500 text-sm">
                      <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ===== INTEGRATIONS ===== */}
        <section className="bg-white py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16 max-w-5xl mx-auto text-center">
            <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
              Conectividad
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-5">
              Se integra con <span className="text-gradient">tus herramientas</span>
            </h2>
            <p className="text-foreground-500 text-base md:text-lg max-w-2xl mx-auto mb-12">
              Conecta Operturi con las plataformas que ya usas. Sin migraciones complicadas ni cambios drásticos.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {operturiIntegrations.map((int) => (
                <div
                  key={int.name}
                  className="card-lift bg-background-50 border border-background-200/60 rounded-2xl p-6 flex flex-col items-center gap-3"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-primary-100 to-secondary-100 text-primary-600">
                    <i className={`${int.icon} text-2xl`} />
                  </div>
                  <span className="font-medium text-foreground-700 text-sm">{int.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== PRICING ===== */}
        <section className="bg-background-50 py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16 max-w-6xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-accent-100 text-accent-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-accent-200/50">
                Precios
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Planes diseñados para{' '}
                <span className="text-gradient">agencias y operadores turísticos</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {operturiPricingPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-6 md:p-8 border transition-all duration-300 ${
                    plan.featured
                      ? 'bg-primary-500 border-primary-500 text-white scale-105 shadow-xl z-10'
                      : 'bg-white border-background-200/60'
                  }`}
                >
                  {plan.featured && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">
                      Más popular
                    </span>
                  )}
                  <h3 className={`font-heading font-semibold text-xl mb-2 ${plan.featured ? 'text-white' : 'text-foreground-900'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm mb-6 ${plan.featured ? 'text-white/70' : 'text-foreground-500'}`}>
                    {plan.description}
                  </p>
                  <div className="mb-6">
                    <span className={`font-heading text-3xl md:text-4xl font-bold ${plan.featured ? 'text-white' : 'text-foreground-900'}`}>
                      {plan.price}
                    </span>
                    <span className={`text-sm ml-1 ${plan.featured ? 'text-white/70' : 'text-foreground-500'}`}>
                      {plan.period}
                    </span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <i className={`ri-check-line mt-0.5 flex-shrink-0 ${plan.featured ? 'text-accent-300' : 'text-accent-500'}`} />
                        <span className={plan.featured ? 'text-white/90' : 'text-foreground-600'}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="https://wa.me/51989307864"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block w-full text-center font-semibold px-6 py-3.5 rounded-xl transition-colors whitespace-nowrap ${
                      plan.featured
                        ? 'bg-white text-primary-600 hover:bg-white/90'
                        : 'bg-primary-500 hover:bg-primary-600 text-white'
                    }`}
                  >
                    {plan.cta}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="bg-white py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16 max-w-4xl mx-auto">
            <div className="text-center mb-14 md:mb-20">
              <span className="inline-block bg-secondary-100 text-secondary-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-secondary-200/50">
                Soporte
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Preguntas <span className="text-gradient">frecuentes</span>
              </h2>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {faqCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setActiveFaqTab(cat); setActiveFaq(null); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    activeFaqTab === cat
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-500 hover:bg-background-100 border border-background-200/60'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {filteredFAQs.map((faq, i) => (
                <div
                  key={faq.question}
                  className="border border-background-200/60 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left transition-colors hover:bg-background-50/50"
                  >
                    <span className="font-medium text-foreground-800 text-sm md:text-base">{faq.question}</span>
                    <i className={`ri-arrow-down-s-line text-xl text-foreground-400 transition-transform duration-300 flex-shrink-0 ${activeFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      activeFaq === i ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <p className="px-5 pb-5 text-foreground-500 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SCREENSHOTS ===== */}
        <section className="bg-background-50 py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16 max-w-6xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
                Interfaz Intuitiva
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Diseñado para ser{' '}
                <span className="text-gradient">fácil de usar</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {operturiScreenshots.map((shot) => (
                <div key={shot.title} className="card-lift bg-white border border-background-200/60 rounded-2xl overflow-hidden group">
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src={shot.image}
                      alt={shot.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-5">
                    <h4 className="font-heading font-semibold text-base text-foreground-900 mb-1">{shot.title}</h4>
                    <p className="text-foreground-500 text-sm">{shot.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== BOTTOM CTA ===== */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 animate-gradient bg-gradient-to-r from-primary-700 via-primary-600 to-secondary-600" />
          <div className="absolute top-10 left-[15%] w-56 h-56 bg-white/5 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 right-[20%] w-72 h-72 bg-accent-500/10 rounded-full blur-3xl animate-float-delay-1" />

          <div className="relative z-10 px-4 md:px-6 lg:px-10 xl:px-16 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
                  ¿Tu agencia aún pierde tiempo{' '}
                  <span className="text-accent-300">gestionando reservas manualmente</span>?
                </h2>
                <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
                  Agenda una demo gratuita de 30 minutos. Te mostraremos cómo Operturi puede transformar la gestión de tu agencia de viajes desde el primer día.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Sesión personalizada sin costo',
                    'Respuesta en menos de 24 horas',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-white/80 text-sm">
                      <i className="ri-check-line text-accent-400" />
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://wa.me/51989307864"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-glow bg-accent-500 hover:bg-accent-600 text-white font-semibold px-8 py-4 rounded-xl inline-flex items-center gap-2 transition-colors whitespace-nowrap text-lg"
                >
                  <i className="ri-whatsapp-line" />
                  Solicitar Demo por WhatsApp
                </a>
              </div>
              <div className="hidden lg:flex justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20travel%20software%20dashboard%20mockup%20on%20laptop%20screen%20side%20view%20floating%20angle%2C%20clean%20minimal%20UI%20with%20reservations%20and%20payments%20modules%2C%20light%20theme%2C%20soft%20pastel%20colors%2C%20professional%20product%20photography%20with%20soft%20shadows%20on%20transparent%20background&width=600&height=450&seq=operturi-cta-mockup&orientation=landscape"
                  alt="Demo de Operturi"
                  className="rounded-2xl shadow-2xl border border-white/20 w-full max-w-md"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}